import { create } from 'zustand';

import { emit } from '../events';
import { onPersonalCounts } from '../learning/LearningService';
import { getSettings } from '../profile/ProfileStore';
import type { Candidate } from './PredictionProvider';
import type { WorkerRequest, WorkerResponse } from './worker/predictionWorker';

/**
 * Main-thread client for the prediction worker.
 *
 * Owns three things the worker cannot: the settings, the request/response pairing, and the decision to
 * stay quiet. Everything ranking-related is in the worker.
 *
 * **Late answers are discarded, not shown.** Each request carries an id and only the newest is
 * rendered. Without that, a slow answer for `wass` could arrive after a fast one for `wasser` and
 * repaint the row with candidates the person has already typed past — which looks like the row
 * flickering at random.
 */

interface PredictionState {
  candidates: Candidate[];
  ready: boolean;
  /** Set when the lexicon could not load. The row stays empty; the app is unaffected. */
  failed: boolean;
  /** Last round trip, for the device check against NFR3's 100 ms. */
  lastElapsedMs: number | null;
  /**
   * The corpus spelling of the word being typed, paired with the word it was computed for.
   *
   * Kept as a pair on purpose. The person may type another letter before the answer lands, and re-casing
   * `wall` with the answer for `wal` would corrupt the word rather than capitalise it — so the caller
   * compares before it applies, and a mismatch means nothing happens.
   */
  casing: { prefix: string; canonical: string | null };
  start: () => void;
  request: (prefix: string, prevWord: string | null) => void;
  clear: () => void;
  accept: (candidate: Candidate) => void;
}

const NO_CASING = { prefix: '', canonical: null } as const;

let worker: Worker | null = null;
let stopCountsSubscription: (() => void) | null = null;
let nextRequestId = 1;
let newestRequestId = 0;

export const usePrediction = create<PredictionState>((set, get) => ({
  candidates: [],
  ready: false,
  failed: false,
  lastElapsedMs: null,
  casing: NO_CASING,

  start: () => {
    if (worker) return;
    // Nothing is loaded while prediction is off: no worker, no fetch, no 1.7 MB in memory. The setting
    // defaults to false and stays false until stage 2 makes the ranking honest.
    if (!getSettings().predictionEnabled) return;

    try {
      worker = new Worker(new URL('./worker/predictionWorker.ts', import.meta.url), {
        type: 'module',
      });
    } catch (err) {
      console.error('[prediction] worker failed to start', err);
      set({ failed: true });
      return;
    }

    worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
      const message = event.data;
      switch (message.type) {
        case 'ready':
          console.info('[prediction] lexicon ready', message.wordCount, 'words', `${message.parseMs}ms`);
          set({ ready: true, failed: false });
          break;
        case 'error':
          // Prediction is an enhancement, not the voice. Losing it must be quiet on screen and loud in
          // the console.
          console.error('[prediction] lexicon unavailable', message.reason);
          set({ failed: true, ready: false, candidates: [] });
          break;
        case 'candidates':
          if (message.requestId !== newestRequestId) return;
          set({
            candidates: message.candidates,
            lastElapsedMs: message.elapsedMs,
            casing: { prefix: message.prefix, canonical: message.canonicalCasing },
          });
          // Read by the flow test, and by the device check that actually settles NFR3's 100 ms.
          (window as unknown as { __sagsPredictionMs?: number }).__sagsPredictionMs = message.elapsedMs;
          break;
      }
    });

    send({ type: 'load', url: '/lexicon/de-1.bin' });

    // The gap stage 1 left: the worker has always accepted a `personal` message and nothing sent it.
    // The main thread owns storage, so it pushes a snapshot — after hydration and after each utterance,
    // which is once per sentence rather than once per keystroke.
    stopCountsSubscription = onPersonalCounts((counts) => {
      send({
        type: 'personal',
        unigrams: [...counts.unigrams],
        bigrams: [...counts.bigrams],
        lastUsed: [...counts.lastUsed],
        display: [...counts.display],
      });
    });
  },

  request: (prefix, prevWord) => {
    if (!worker || !get().ready) return;
    const settings = getSettings();
    if (!settings.predictionEnabled) return;

    newestRequestId = nextRequestId;
    nextRequestId += 1;
    send({
      type: 'suggest',
      requestId: newestRequestId,
      prefix,
      prevWord,
      count: settings.candidateCount,
      weights: settings.predictionWeights,
      nowMs: Date.now(),
    });
  },

  clear: () => set({ candidates: [], casing: NO_CASING }),

  accept: (candidate) => {
    // Recorded by slot, not by word: §5.5 wants the slot distribution, because a slot 1 that is never
    // chosen while 2–4 are is evidence of inattention to the left of the band. `DiagnosticsService`
    // already stores this event and strips everything else.
    emit({ type: 'PredictionAccepted', slot: candidate.slot });
    // The accepted word already carries the corpus casing, so any pending answer for the partial word
    // it replaced is not just stale but about a word that no longer exists.
    set({ candidates: [], casing: NO_CASING });
  },
}));

function send(message: WorkerRequest): void {
  worker?.postMessage(message);
}

/** Test seam. */
export function stopPredictionWorker(): void {
  stopCountsSubscription?.();
  stopCountsSubscription = null;
  worker?.terminate();
  worker = null;
  nextRequestId = 1;
  newestRequestId = 0;
}
